import { useEffect, useState } from 'react';
import { formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { Button, Card, Chip, Field, Input, NumberInput, useConfirm, useToast } from '../ui';

/**
 * حقوق و دستمزد (payroll) — employee master + a monthly run that computes insurance + salary tax and,
 * once posted, books to the ledger. Rates are illustrative defaults (confirm the current year).
 * Finance/Admin (accounting.post to add employees / run / post).
 */

interface Employee {
  id: number;
  code: string;
  name: string;
  baseSalary: number;
}
interface Run {
  id: number;
  no: number;
  ym: string;
  status: string;
  gross: number;
  employeeInsurance: number;
  employerInsurance: number;
  incomeTax: number;
  net: number;
}

export function PayrollPanel({ onPosted }: { onPosted?: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [emps, setEmps] = useState<Employee[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [salary, setSalary] = useState(0);
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));

  function refresh() {
    void api<{ employees: Employee[] }>('/api/accounting/employees').then((r) => {
      if (r.ok) setEmps((r.body as { employees: Employee[] }).employees);
    });
    void api<{ runs: Run[] }>('/api/accounting/payroll').then((r) => {
      if (r.ok) setRuns((r.body as { runs: Run[] }).runs);
    });
  }
  useEffect(refresh, []);

  async function addEmployee() {
    if (!name.trim() || salary < 0) {
      toast('نام و حقوق پایه لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api('/api/accounting/employees', {
      method: 'POST',
      body: JSON.stringify({ code, name, baseSalary: salary }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r, 'خطا'), 'error');
    toast('کارمند افزوده شد ✓');
    setName('');
    setCode('');
    setSalary(0);
    refresh();
  }

  async function runPayroll() {
    setBusy(true);
    const r = await api('/api/accounting/payroll', {
      method: 'POST',
      body: JSON.stringify({ ym }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r, 'خطا در ساخت لیست حقوق'), 'error');
    toast('لیست حقوق ساخته شد ✓');
    refresh();
  }

  async function post(id: number) {
    if (
      !(await confirm({
        title: 'ثبت حقوق در دفترکل',
        message: 'لیست حقوق این دوره در دفترکل ثبت می‌شود (قابل بازگشت فقط با سند برگشتی). ادامه؟',
        danger: true,
      }))
    )
      return;
    setBusy(true);
    const r = await api(`/api/accounting/payroll/${id}/post`, { method: 'POST' });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r, 'خطا در ثبت'), 'error');
    toast('لیست حقوق به دفترکل ثبت شد ✓');
    refresh();
    onPosted?.();
  }

  return (
    <Card title="حقوق و دستمزد">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
          gap: 10,
          alignItems: 'end',
        }}
      >
        <Field label="کد">
          <Input value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label="نام کارمند">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="حقوق پایه (ریال)">
          <NumberInput value={salary} onValue={setSalary} />
        </Field>
        <Button variant="secondary" onClick={() => void addEmployee()} disabled={busy}>
          افزودن کارمند
        </Button>
      </div>

      {emps.length > 0 ? (
        <p style={{ color: 'var(--ink-3)', marginTop: 8 }}>
          {emps.length} کارمند فعال · جمع حقوق پایه:{' '}
          {formatRial(emps.reduce((s, e) => s + e.baseSalary, 0))}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'end', marginTop: 12, flexWrap: 'wrap' }}>
        <Field label="ماه (YYYY-MM)">
          <Input value={ym} onChange={(e) => setYm(e.target.value)} placeholder="2026-07" />
        </Field>
        <Button onClick={() => void runPayroll()} disabled={busy}>
          ساخت لیست حقوق ماه
        </Button>
      </div>

      {runs.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>ماه</th>
                <th>ناخالص</th>
                <th>بیمه (کارگر)</th>
                <th>مالیات</th>
                <th>خالص</th>
                <th>وضعیت</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{r.ym}</td>
                  <td>{formatRial(r.gross)}</td>
                  <td>{formatRial(r.employeeInsurance)}</td>
                  <td>{formatRial(r.incomeTax)}</td>
                  <td>
                    <b>{formatRial(r.net)}</b>
                  </td>
                  <td>
                    <Chip tone={r.status === 'posted' ? 'success' : 'neutral'}>
                      {r.status === 'posted' ? 'ثبت‌شده' : 'پیش‌نویس'}
                    </Chip>
                  </td>
                  <td>
                    {r.status === 'draft' ? (
                      <Button variant="secondary" onClick={() => void post(r.id)} disabled={busy}>
                        ثبت در دفترکل
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}
